import clsx from 'clsx';
import { Input, InputProps } from '@clover/web-lib-consumer-ui';
import { usePropSplit } from 'hooks/usePropSplit';
import { MENU_ITEM_MODAL_TEST_ID } from 'routes/MenuItemModal/MenuItemModalTestIds';
import React, {
  HTMLAttributes,
  useRef,
  memo,
  ReactNode,
} from 'react';
import { KEY } from 'util/Input';

import styles from './EnumInput.module.scss';

// All the input props we want to hoist to the top-level of props instead of nesting in `inputProps`
const HOISTED_PROPS = [
  'aria-describedby',
  'customError',
  'disabled',
  'onBlur',
  'onChange',
  'onFocus',
  'onInvalid',
  'onInvalid',
  'readOnly',
  'required',
  'validateField',
] as const;

// All the props we don't want to allow. Usually because we want to override them or define them differently.
const OMITTED_PROPS = ['textfield', 'value', 'type', 'name', 'checked'] as const;

// A string union type out of the above HOISTED_PROPS list
// See https://stackoverflow.com/questions/45251664/typescript-derive-union-type-from-tuple-array-values
type HoistedPropNames = typeof HOISTED_PROPS[number];
type OmittedPropNames = typeof OMITTED_PROPS[number];

type AllowedInputProps = Omit<InputProps, OmittedPropNames>
type HoistedProps = Pick<AllowedInputProps, HoistedPropNames>;

type EnumInputProps = Omit<HTMLAttributes<HTMLElement>, HoistedPropNames | OmittedPropNames> & HoistedProps & {
  type: 'radio' | 'checkbox';
  label: string | ReactNode; // This selection's name (enum name)
  labelClassName?: string;
  name: string; // The group name
  value?: string | number; // Defaults to same value as `label`. The 'enum' value.
  checked: boolean;
  inputProps?: AllowedInputProps;
};

/**
 * Radio OR Checkbox root component.
 * The native input element will handle all aria-roles and keyboard interaction automatically.
 *
 * Usage Rules:
 * - All radio/checkboxes in a group must have the same 'name' value
 * - All radio/checkboxes in a group must be wrapped in a fieldset.
 */
const EnumInputImpl: React.FC<EnumInputProps> = (props => {
  const { type = 'checkbox', label, labelClassName, name, value, className, checked, inputProps, onKeyDown, children, ...remainingProps } = props;

  const [passThroughContainerProps, hoistedInputProps] = usePropSplit<HoistedProps, typeof remainingProps>(remainingProps, HOISTED_PROPS);
  const inputElmRef = useRef<HTMLInputElement | null>(null);
  const inputPassthroughProps = { ...inputProps, ...hoistedInputProps };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KEY.ENTER) {
      // Prevent submitting the form when 'ENTER' is hit
      e.preventDefault();
    }

    if (onKeyDown) onKeyDown(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (inputPassthroughProps.disabled) e.preventDefault();
    else inputPassthroughProps.onChange?.(e);
  };

  // Label wraps the input so the entire text is clickable
  // See https://schoberg.net/2008/10/making-better-forms-clickable-text-for-radio-button-and-checkbox-fields/
  return (
    <label className={clsx(styles.inputWrapper, className)} {...passThroughContainerProps}>
      <Input
        {...inputPassthroughProps}
        ref={inputElmRef}
        type={type}
        name={name}
        checked={checked}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={value}
        validateOnBlur={true}
      />
      {/* The span is used to create the styled checkbox */}
      <span aria-hidden={'true'}>&nbsp;</span>
      <span data-testid={MENU_ITEM_MODAL_TEST_ID.modifierName} className={clsx(styles.labelText, inputPassthroughProps.disabled && styles.disabled, labelClassName)}>{label}</span>
      {children}
    </label>
  );
});

// This is for a more friendly debugger experience; without this you get Anonymous memo
// because the transpiler doesn't know the function name
export const EnumInput = memo(EnumInputImpl);

export const RadioButton: React.FC<Omit<EnumInputProps, 'type'>> = (props) => (
  <EnumInput type="radio" {...props} />
);

export const Checkbox: React.FC<Omit<EnumInputProps, 'type'>> = (props) => (
  <EnumInput type="checkbox" {...props} />
);
